/**
 * The hero at the top of `/ask`.
 *
 * `/ask` is the default landing route and the primary surface of the product,
 * so it opens with a statement rather than a form label. The headline uses the
 * brand's condensed display face (`display-*` — Turbine, tight leading,
 * negative tracking); nothing else in the portal does, which is precisely why
 * this one moment reads as the front door and a ticket list does not.
 *
 * Two sizes, one element. Once a conversation is on screen the transcript is
 * the thing being read, so the hero shrinks and drops its supporting line
 * instead of unmounting — the `<h1>` stays put across both states, and the
 * page never swaps its heading structure underneath a screen reader.
 *
 * The headline is split into two keys so the emphatic tail can carry the
 * accent colour. Translators own both halves and can move the weight of the
 * sentence between them.
 *
 * The supporting line follows `ASSISTANT_MODE`. In `intake` mode there is no
 * troubleshooting to promise, so it invites the employee to describe the issue
 * and says it will be raised quickly instead.
 */

import { cn, Typography } from "@norican/react";

import { useT } from "@/i18n";

export interface AskHeroProps {
  /** Shrink to a page heading. Set once there is a transcript to read. */
  compact?: boolean;
  /** Use the fast-to-ticket supporting line (`ASSISTANT_MODE=intake`). */
  intake?: boolean;
  className?: string;
}

export function AskHero({ compact = false, intake = false, className }: AskHeroProps) {
  const { t } = useT("assistant");

  return (
    <header className={cn("flex flex-col", compact ? "gap-3" : "gap-4 pb-2", className)}>
      <Typography
        as="h1"
        variant="display-lg"
        className={cn(
          // `text-*` carries Tailwind's own line-height with it, which would
          // undo the display scale's tight leading. `leading-display` (0.96)
          // puts the brand value back at every breakpoint.
          "leading-display text-balance",
          // Sized down ~12% on review: the welcome was shouting over the
          // question box, which is the thing the page is actually for.
          compact ? "text-2xl sm:text-3xl" : "text-[2rem] sm:text-[2.75rem]",
        )}
      >
        {t("hero.headline")} <span className="text-accent">{t("hero.headlineAccent")}</span>
      </Typography>

      {compact ? null : (
        <Typography
          variant="body-lg"
          tone="muted"
          // No width cap. The supporting line is one sentence and belongs on
          // one line at desktop widths; the column's own max-width is the only
          // constraint it needs, and `text-pretty` handles the wrap when a
          // narrow viewport genuinely forces one.
          className="font-bold text-pretty"
        >
          {intake ? t("subtitleIntake") : t("subtitle")}
        </Typography>
      )}
    </header>
  );
}
